import TForm from '@rjsf/core';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync, useAsyncFn, useToggle } from 'react-use';
import cloneDeep from 'lodash/cloneDeep';

import PageTitle from '@/components/shared/utillities/PageTitle';
import { useApplicationsStore } from '@/context/applications';
import Feature from '@/domain/core/Feature';
import PopupNotification from '@/domain/core/PopupNotification';
import User from '@/domain/core/User';
import { SelectionDTO } from '@/features/admin/selections/dtos/selection';
import { ConfirmEmailModal } from '@/features/personalAccount/views/ConfirmEmailModal';
import {
	IApplication,
	IApplicationFormProps,
	TSchemaType
} from '@/types/applications';
import { Button } from '@/ui/Button';
import { Label } from '@/ui/Label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';
import { backendApi } from '@/utils/api';
import { ApplicationCloneModal } from '../ApplicationCloneModal';
import { ApplicationsEditForm } from '../ApplicationsEditForm';
import { ApplicationClosedWarningModal } from '@/features/applications/ApplicationClosedWarningModal';
import { AutosaveRestoreModal } from '@/features/autosave/components/AutosaveRestoreModal';
import { AutosaveSourceBanner } from '@/features/autosave/components/AutosaveSourceBanner';
import { transformErrors } from '@/features/shared/utils/transformApplicationErrors';
import cn from './style.module.sass';
import { AxiosError } from 'axios';
import { TutorialIcon } from '@/features/tutorial/views/TutorialIcon';
import { TutorialSectionInSelection } from '@workspace/shared-constants';
import { useApplicationAutosaveBridge } from './hooks/useApplicationAutosaveBridge';
import { ExceptionModel } from '@/api/generated/models';

const prepareDraftData = (data: Record<string, any>) => {
	const result = cloneDeep(data ?? {});

	const isValidValue = (value: any) => value !== null && value !== undefined;

	const keys = Object.keys(result);

	for (const key of keys) {
		// Если массив, удаляем "пустые" элементы
		if (Array.isArray(result[key])) {
			result[key] = result[key].filter(isValidValue);
			continue;
		}

		// Удаляем "пустые" поля
		if (!isValidValue(result[key])) {
			delete result[key];
		}
	}

	return result;
};

const ApplicationsLayout: React.VFC<IApplicationFormProps> = observer(props => {
	const {
		mode,
		title,
		selectionId,
	} = props;

	const { t } = useTranslation();
	const { query, push } = useRouter();

	const form = useRef<TForm>(null);
	const data = useRef(null);

	const [schemaType, setSchemaType] = useState<TSchemaType>('create');

	const [isOpenEmailConfirm, setOpenEmailConfirm] = useToggle(false);
	const initialFormData = 'formDataInitial' in props ? props.formDataInitial : undefined;

	const initialEmptyFormData = useMemo<IApplication>(
		() =>
			mode === 'new'
				? {
						...(query?.nominationId ? { direction: query?.nominationId } : {}),
					}
				: initialFormData,
		[initialFormData, mode, query?.nominationId]
	);

	const [baselinePayload, setBaselinePayload] = useState<IApplication>(() => cloneDeep(initialEmptyFormData));
	const [formData, setFormData] = useState<IApplication>(initialEmptyFormData);
	const [formResetKey, setFormResetKey] = useState(0);

	const [clone, setClone] = useState(false);

	const {
		templateId,
		schema,
		groups,
		tutorialsFields,
		handleApplicationCreate,
		handleApplicationUpdate,
		handleApplicationDraftCreate,
		handleApplicationDraftUpdate,
		handleApplicationDraftApply
	} = useApplicationsStore();

	const {
		loading: themesLoading,
		value: selection,
		error: themesError,
	} = useAsync(async () => {
		const { data } = await backendApi.get<SelectionDTO>(`/v2/selection/${selectionId}`);

		return data;
	}, []);

	const [{ loading: createLoading }, handleCreate] = useAsyncFn(
		async ({ formData }) => {
			if (mode === 'draft') {
				try {
					await handleApplicationDraftApply({ data: formData, id: props.id });

					PopupNotification.emitter(
						'success',
						t('application.created', { name: formData.projectName })
					);

					await push('/projects');
				} catch (error) {
					PopupNotification.emitter('error', t('templates.saveError', { name: formData.projectName }));

					console.error(error);
				}
			} else {
				try {
					await handleApplicationCreate({
						data: formData,
						parentId: query?.parentId ? +query?.parentId : undefined,
					});
					await autosave.clearDraftAfterMainSave();
					await push('/projects');

					PopupNotification.emitter('success', t('application.created', {name: formData.projectName}));
				} catch (error) {
					PopupNotification.emitter('error', t('templates.saveError', {name: formData.projectName}));

					console.error(error);
				}
			}
		},
		[query?.parentId]
	);

	const [{ loading: updateLoading }, handleUpdate] = useAsyncFn(
		async ({ formData }) => {
			if (mode !== 'project') return;

			try {
				await handleApplicationUpdate({ data: formData, id: props.id });
				await autosave.clearDraftAfterMainSave();

				PopupNotification.emitter('success', t('application.saved', { name: formData.projectName }));

				await push('/projects');
			} catch (e) {
				const error = e as AxiosError<ExceptionModel>;

				if (
					error?.response?.data?.message?.includes(
						'You cannot perform this action, because action disabled in business process'
					)
				) {
					PopupNotification.emitter('error', t('application.editForbidden'));
				} else {
					PopupNotification.emitter('error', t('application.saveError', { name: formData.projectName }));
				}
				console.error(error);
			}
		},
		[query?.parentId]
	);

	const [{ loading: draftLoading }, handleDraft] = useAsyncFn(async ({ formData }) => {
		if (mode === 'draft') {
			try {
				await handleApplicationDraftUpdate({ data: prepareDraftData(formData), id: props.id });

				PopupNotification.emitter(
					'success',
					t('drafts.created', { name: formData.projectName })
				);

				await push('/drafts');
			} catch (error) {
				PopupNotification.emitter(
					'error',
					t('drafts.createError', { name: formData.projectName })
				);

				console.error(error);
			}
		} else {
			try {
				await handleApplicationDraftCreate({data: prepareDraftData(formData) as IApplication});
				await autosave.clearDraftAfterMainSave();

				PopupNotification.emitter('success', t('drafts.created', {name: formData.projectName}));

				await push('/drafts');
			} catch (error) {
				PopupNotification.emitter('error', t('drafts.createError', {name: formData.projectName}));

				console.error(error);
			}
		}
	}, []);

	const { loading: cloneLoading } = useAsync(async () => {
		try {
			if (query?.parentId) {
				const { data: formData } = await backendApi.get<IApplication & { direction: { id: number } }>(
					`/v2/applications/${query?.parentId}`
				);

				const data = Object.entries({
					projectName: formData.name,
					direction: formData?.direction?.id,
					...formData?.info,
				}).reduce((result, [key, value]) => {
					result[key.replace(/FileId$|FileIds$/gi, '')] = value;

					return result;
				}, {});

				setFormData(data);
				setBaselinePayload(cloneDeep(data));

				PopupNotification.emitter('success', t('application.fieldsFilled'));
			}
		} catch (error) {
			PopupNotification.emitter('error', t('application.cloneError'));

			console.error(error);
		}
	}, [query?.parentId]);

	const {
		autosave,
		bannerProps,
		formAutosaveProps,
		restoreModalProps,
	} = useApplicationAutosaveBridge({
		baselinePayload,
		cloneLoading,
		formData,
		initialEmptyFormData,
		mode,
		projectId: mode === 'project' ? props.id : null,
		selectionId,
		setFormData,
		setFormResetKey,
	});

	const isSendPossible = User.isInternalUser || User.isEmailConfirmed;

	const onSubmit = useCallback(
		(form) => {
			if (schemaType === 'create' && mode === 'project') {
				return handleUpdate(form);
			}

			data.current = form;

			if (schemaType === 'create') {
				if (Feature.isFeatureEnabled('newConsents') && !isSendPossible && !isOpenEmailConfirm) {
					setOpenEmailConfirm(true);

					return;
				}

				handleCreate(form);
			}

			if (schemaType === 'draft') {
				handleDraft(form);
			}
		},
		[schemaType, isOpenEmailConfirm]
	);

	const handleSubmit = useCallback((schemaType: TSchemaType) => {
		setSchemaType(schemaType);
		setTimeout(() => form.current?.formElement?.current?.requestSubmit?.(), 5);
	}, []);

	const formContext = useMemo(
		() => {
			if (mode === 'new') {
				return {
					selectionId,
					groups,
					descriptionLocation: 'below',
					tutorialsFields: tutorialsFields
				}
			}

			return {
				selectionId,
				groups,
				initiatorId: props.initiatorId,
				tutorialsFields: tutorialsFields
			}
		},
		[selectionId, groups, templateId, props]
	);

	return (
		<div className="flex flex-col gap-8">
			<div className={cn.top}>
				<div className={cn.title}>
					<PageTitle>
						{title}
						<TutorialIcon section={TutorialSectionInSelection.projectsApplication} selectionId={selectionId}/>
					</PageTitle>
				</div>
			</div>

			<div className={clsx(cn.body, 'w-full flex flex-col gap-8 bg-white rounded-2xl p-12 mb-3')}>
				{!cloneLoading && (
					<AutosaveSourceBanner
						activeSource={bannerProps.activeSource}
						canSwitchSource={bannerProps.canSwitchSource}
						draftUpdatedAt={bannerProps.draftUpdatedAt}
						hasAutosaveSource={bannerProps.hasAutosaveSource}
						hasBaselineSource={bannerProps.hasBaselineSource}
						hasEmptySource={bannerProps.hasEmptySource}
						onSwitchSource={bannerProps.onSwitchSource}
					/>
				)}

				{mode === 'new' &&
					<div className="flex flex-wrap justify-between">
						<div className="relative text-2xl">{t('passports.category.about')}</div>

						{!query?.parentId && Feature.isFeatureEnabled('applicationsPrefilling') && (
							<Button onClick={() => setClone(true)} variant="tetriary">
								{t('application.fieldsFill')}
							</Button>
						)}
					</div>
				}

				<div className={clsx(cn.bottom, 'flex flex-col gap-8')}>
					<div>
						<Label>
							{t('project.theme')} <span className="text-red-500">*</span>
						</Label>

						<Select disabled value={`${selectionId}`}>
							<SelectTrigger className="w-full">
								{themesLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								{!themesLoading && <SelectValue placeholder={t('common.selectList')} />}
								<SelectValue placeholder={t('common.selectList')} />
							</SelectTrigger>

							<SelectContent>
								<SelectGroup>
									{selection && (
										<SelectItem key={selection.id} value={`${selection.id}`}>
											{selection.name}
										</SelectItem>
									)}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>

					{cloneLoading && (
						<div className="flex gap-2 flex-row items-center">
							<Loader2 className="h-4 w-4 animate-spin" />

							<span>{t('application.pff')}</span>
						</div>
					)}

					{!cloneLoading && (
						<>
							<div
								onChangeCapture={formAutosaveProps.onChangeCapture}
								onInputCapture={formAutosaveProps.onInputCapture}
								onKeyDownCapture={formAutosaveProps.onKeyDownCapture}
							>
								<ApplicationsEditForm<IApplication>
									key={`application-form-${formResetKey}`}
									id="application"
									ref={form}
									schema={schema[schemaType] ?? schema.create}
									onSubmit={onSubmit}
									formContext={formContext}
									formData={formData}
									onFormDataChange={formAutosaveProps.onFormDataChange}
									resetKey={formResetKey}
									selection={selection}
									transformErrors={transformErrors(schemaType, schema[schemaType], t)}
								/>
							</div>
						</>
					)}
				</div>

				{mode !== 'project' && selection && selection.isOpened &&
					<div className="flex flex-wrap flex-row gap-4">
						<Button
							data-testid="button-new-application-save"
							gradient
							onClick={() => handleSubmit('create')}
							disabled={createLoading || draftLoading || cloneLoading}
						>
							{createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							{t('application.send')}
						</Button>

						<Button
							data-testid="button-new-application-save-draft"
							variant="secondary"
							onClick={() => handleSubmit('draft')}
							disabled={createLoading || draftLoading || cloneLoading}
						>
							{draftLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							{t('drafts.save')}
						</Button>
					</div>
				}
				{mode === 'project' &&
					<div className="flex flex-row gap-4">
						<Button gradient onClick={() => handleSubmit('create')} disabled={updateLoading}>
							{updateLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							{t('application.save')}
						</Button>
					</div>
				}
			</div>

			<div />

			{mode !== 'project' &&
				<>
					<ApplicationClosedWarningModal selection={selection}/>

					<ApplicationCloneModal isOpen={clone} onCloseHandler={() => setClone(false)} />

					<ConfirmEmailModal
						isOpen={isOpenEmailConfirm && !isSendPossible}
						onCloseHandler={setOpenEmailConfirm}
						onSuccess={() => handleCreate(data.current)}
					/>
				</>
			}

			<AutosaveRestoreModal
				open={restoreModalProps.open}
				allowEmptySource={restoreModalProps.allowEmptySource}
				draftUpdatedAt={restoreModalProps.draftUpdatedAt}
				onKeepBaseline={restoreModalProps.onKeepBaseline}
				onRestoreAutosave={restoreModalProps.onRestoreAutosave}
				onStartEmpty={restoreModalProps.onStartEmpty}
			/>
		</div>
	);
});

export { ApplicationsLayout };
